import { Injectable } from '@angular/core';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { user as userData } from 'app/mock-api/common/user/data';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class AuthMockApi {
    private readonly _secret: any;
    private _user: any = userData;
    private _magicLinks: Map<string, string> = new Map(); //Stores email -> token mapping

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService) {
        // Set the mock-api
        this._secret =
            'YOUR_VERY_CONFIDENTIAL_SECRET_FOR_SIGNING_JWT_TOKENS!!!';

        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ SendM Magic Link to Sign In- POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onPost('api/auth/magic-link', 1000).reply(({ request }) => {
            const { email } = request.body;
    
            if (email) {
                const token = this._generateMagicToken(email);
                localStorage.setItem(`magic_link_${email}`, token);
    
                // ✅ Print the mock magic link to the console
                const magicLinkUrl = `http://localhost:4200/sign-in?oobCode=${token}`;
                console.log(`🚀 Mock Magic Link for ${email}: ${magicLinkUrl}`);
    
                return [200, { message: 'Magic link sent', token }];
            }
    
            return [400, { error: 'Invalid email' }];
        });

        // -----------------------------------------------------------------------------------------------------
        // @ Sign in with the magic link - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onPost('api/auth/sign-in-with-magic-link').reply(({ request }) => {
            const { token } = request.body;
            const email = this._verifyMagicToken(token);
            if (email) {
                const storedToken = localStorage.getItem(`magicLinkEmail`);
                if (storedToken === token) {
                    this._magicLinks.delete(email); // Invalidate token after use

                    // 🟡 Inject the actual email into the mock user
                    this._user.email = email;
        
                    return [
                        200,
                        {
                            user: cloneDeep(this._user),
                            accessToken: this._generateJWTToken(),
                            tokenType: 'bearer',
                        },
                    ];
                }
            }
    
            return [401, { error: 'Invalid or expired magic link' }];
        });
        
        // -----------------------------------------------------------------------------------------------------
        // @ Sign in using the access token - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/auth/sign-in-with-token')
            .reply(({ request }) => {
                // Get the access token
                const accessToken = request.body.accessToken;

                // Verify the token
                if (this._verifyJWTToken(accessToken)) {
                    return [
                        200,
                        {
                            user: cloneDeep(this._user),
                            accessToken: this._generateJWTToken(),
                            tokenType: 'bearer',
                        },
                    ];
                }

                // Invalid token
                return [
                    401,
                    {
                        error: 'Invalid token',
                    },
                ];
            });
        // -----------------------------------------------------------------------------------------------------
        // @ Sign in with OTP - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/auth/sign-in-with-otp')
            .reply(({ request }) => {
                const { email, otp } = request.body;

                if (!email || !otp) {
                    return [400, { error: 'Missing email or OTP' }];
                }

                // For mock: Accept '123456' as valid OTP
                if (otp === '123456') {
                    this._user.email = email; // Inject email into mock user
                    return [
                        200,
                        {
                            user: cloneDeep(this._user),
                            accessToken: this._generateJWTToken(),
                            tokenType: 'bearer',
                        },
                    ];
                }

                return [401, { error: 'Invalid OTP' }];
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Generates a one-time magic link token for authentication.
     *
     * The token is a Base64 URL-encoded string containing an email and expiration timestamp.
     * It is signed using HMAC-SHA256 to prevent tampering.
     *
     * @param {string} email - The email address of the user requesting the magic link.
     * @returns {string} A signed magic link token in the format `payload.signature`.
     *
     * @example
     * const token = _generateMagicToken("user@example.com");
     * console.log(token); // "eyJlbWFpbCI6InVzZXJAZXhhbXBsZS5jb20iLCJleHAiOjE3MDEzNTkwMDB9.signature"
     */
    private _generateMagicToken(email: string): string {
        const payload = { email, exp: Math.floor(Date.now() / 1000) + 600 }; // Expires in 10 min
        const stringifiedPayload = Utf8.parse(JSON.stringify(payload));
        const encodedPayload = this._base64url(stringifiedPayload);
        const signature = this._base64url(HmacSHA256(encodedPayload, this._secret));
        return encodedPayload + '.' + signature;
    }

    /**
     * Verifies the magic link token and extracts the associated email if valid.
     *
     * This function checks the integrity and validity of the token by:
     * 1. Splitting it into payload and signature parts.
     * 2. Recomputing the signature using the stored secret and comparing it.
     * 3. Decoding the payload to check expiration and extract the email.
     *
     * @param {string} token - The magic link token received from the client.
     * @returns {string | null} The email associated with the token if valid, otherwise `null`.
     *
     * @example
     * const email = _verifyMagicToken("eyJlbWFpbCI6InVzZXJAZXhhbXBsZS5jb20iLCJleHAiOjE3MDEzNTkwMDB9.signature");
     * console.log(email); // "user@example.com" (if token is valid) or null (if invalid/expired)
     */
    private _verifyMagicToken(token: string): string | null {
        const parts = token.split('.');
        if (parts.length !== 2) return null;

        const payload = parts[0];
        const signature = parts[1];

        if (this._base64url(HmacSHA256(payload, this._secret)) !== signature) return null;

        const decodedPayload = JSON.parse(Utf8.stringify(Base64.parse(payload)));
        if (decodedPayload.exp < Math.floor(Date.now() / 1000)) return null; // Expired token

        return decodedPayload.email;
    }

    /**
     * Return base64 encoded version of the given string
     *
     * @param source
     * @private
     */
    private _base64url(source: any): string {
        // Encode in classical base64
        let encodedSource = Base64.stringify(source);

        // Remove padding equal characters
        encodedSource = encodedSource.replace(/=+$/, '');

        // Replace characters according to base64url specifications
        encodedSource = encodedSource.replace(/\+/g, '-');
        encodedSource = encodedSource.replace(/\//g, '_');

        // Return the base64 encoded string
        return encodedSource;
    }

    /**
     * Generates a JWT token using CryptoJS library.
     *
     * This generator is for mocking purposes only and it is NOT
     * safe to use it in production frontend applications!
     *
     * @private
     */
    private _generateJWTToken(): string {
        // Define token header
        const header = {
            alg: 'HS256',
            typ: 'JWT',
        };

        // Calculate the issued at and expiration dates
        const date = new Date();
        const iat = Math.floor(date.getTime() / 1000);
        const exp = Math.floor(date.setDate(date.getDate() + 7) / 1000);

        // Define token payload
        const payload = {
            iat: iat,
            iss: 'Fuse',
            exp: exp,
        };

        // Stringify and encode the header
        const stringifiedHeader = Utf8.parse(JSON.stringify(header));
        const encodedHeader = this._base64url(stringifiedHeader);

        // Stringify and encode the payload
        const stringifiedPayload = Utf8.parse(JSON.stringify(payload));
        const encodedPayload = this._base64url(stringifiedPayload);

        // Sign the encoded header and mock-api
        let signature: any = encodedHeader + '.' + encodedPayload;
        signature = HmacSHA256(signature, this._secret);
        signature = this._base64url(signature);

        // Build and return the token
        return encodedHeader + '.' + encodedPayload + '.' + signature;
    }

    /**
     * Verify the given token
     *
     * @param token
     * @private
     */
    private _verifyJWTToken(token: string): boolean {
        // Split the token into parts
        const parts = token.split('.');
        const header = parts[0];
        const payload = parts[1];
        const signature = parts[2];

        // Re-sign and encode the header and payload using the secret
        const signatureCheck = this._base64url(
            HmacSHA256(header + '.' + payload, this._secret)
        );

        // Verify that the resulting signature is valid
        return signature === signatureCheck;
    }
}
